import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  askHelenaConversationId,
  chatSearchQueryAtom,
  isChatSearchOpenAtom,
  openAskHelenaConversation,
  selectedConversationIdAtom,
} from "../store";

/** Query param carrying the open Ask Helena conversation, e.g. `?conversation=<id>`. */
const CONVERSATION_QUERY_PARAM = "conversation";
/**
 * Query param carrying the search panel: present means the panel is open, its
 * value is the term. An empty `?q=` is the panel with nothing typed yet.
 */
const SEARCH_QUERY_PARAM = "q";

/**
 * Keeps the URL and the open Ask Helena panel in step, so a thread — or a
 * search — can be shared or reloaded.
 *
 * The two params never coexist: search covers the whole panel, so while it is
 * open the conversation underneath is not what the URL describes.
 *
 * Reading the URL happens once per mount — after that the atoms are the source
 * of truth and the URL only follows them, `replace`d so a chat session does not
 * fill the back stack.
 *
 * The live id is preferred over the selected one because a first question gets
 * its id from the `conversation` SSE event, not from a sidebar click.
 */
export const useAskHelenaConversationUrlSync = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const liveConversationId = useAtomValue(askHelenaConversationId);
  const selectedConversationId = useAtomValue(selectedConversationIdAtom);
  const isSearchOpen = useAtomValue(isChatSearchOpenAtom);
  const searchQuery = useAtomValue(chatSearchQueryAtom);
  const openConversation = useSetAtom(openAskHelenaConversation);
  const setIsSearchOpen = useSetAtom(isChatSearchOpenAtom);
  const setSearchQuery = useSetAtom(chatSearchQueryAtom);

  // Search owns the panel while it is open, so it owns the URL too.
  const conversationId = isSearchOpen
    ? null
    : (liveConversationId ?? selectedConversationId);
  const searchTerm = isSearchOpen ? searchQuery : null;

  const urlConversationId = searchParams.get(CONVERSATION_QUERY_PARAM);
  const urlSearchTerm = searchParams.get(SEARCH_QUERY_PARAM);

  // Captured on the first render: later renders read the params the atoms wrote.
  const initialUrlStateRef = useRef({
    conversationId: urlConversationId,
    searchTerm: urlSearchTerm,
  });
  // Blocks the writer until the deep link has been applied — without it the
  // writer would see the still-empty atoms and strip the params off the URL.
  const [isRestored, setIsRestored] = useState(
    initialUrlStateRef.current.conversationId === null &&
      initialUrlStateRef.current.searchTerm === null,
  );

  useEffect(() => {
    const initial = initialUrlStateRef.current;

    // Search first: with `?q=` in the URL the panel was left searching, and the
    // thread it was covering is not part of that state.
    if (initial.searchTerm !== null) {
      setSearchQuery(initial.searchTerm);
      setIsSearchOpen(true);
    } else if (initial.conversationId) {
      openConversation(initial.conversationId);
    } else {
      return;
    }

    setIsRestored(true);
  }, [openConversation, setIsSearchOpen, setSearchQuery]);

  useEffect(() => {
    if (!isRestored) return;
    if (conversationId === urlConversationId && searchTerm === urlSearchTerm) {
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (conversationId) {
          next.set(CONVERSATION_QUERY_PARAM, conversationId);
        } else {
          next.delete(CONVERSATION_QUERY_PARAM);
        }
        if (searchTerm === null) {
          next.delete(SEARCH_QUERY_PARAM);
        } else {
          next.set(SEARCH_QUERY_PARAM, searchTerm);
        }
        return next;
      },
      { replace: true },
    );
  }, [
    conversationId,
    isRestored,
    searchTerm,
    setSearchParams,
    urlConversationId,
    urlSearchTerm,
  ]);
};
